import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Component,
  inject,
  OnDestroy,
  OnInit,
  DestroyRef
} from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import {
  IsActiveMatchOptions,
  NavigationEnd,
  Router,
  RouterModule
} from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthUserService } from '../../../core/services/auth-user.service';
import { SharedModuleService } from '../../../core/services/shared-module.service';
import { SideBarToggleService } from '../../../core/services/side-bar-toggle.service';
import { InviteUpdateSidebarComponent } from '../../../modules/broker/broker-admin/invite-update-sidebar/invite-update-sidebar.component';
import { USERROLES } from '../../../core/common-enums/enum';
import { SidebarToggleResponse } from '../../../core/models/broker-admin';
import { CommonModule } from '@angular/common';
import { PanelMenuModule } from 'primeng/panelmenu';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { DividerModule } from 'primeng/divider';
import { MenuItem } from 'primeng/api';

interface LogoData {
  img: string;
  tagline: string;
  base64Image: string;
}

interface RoleDetailsStorage {
  subDomainName?: string;
}

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    PanelMenuModule,
    MenuModule,
    ButtonModule,
    DrawerModule,
    DividerModule
  ]
})
export class SidebarComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  profileScore!: string;
  userId!: string;
  clientId!: string;
  flag: boolean = false;
  role: string = '';
  roleRoute: string = '';
  state?: number;
  selectedReport?: string;
  selectedModal?: string;
  subheading: string = '';
  heading: string = '';
  isReportsActive: boolean = false;
  isActive: boolean = false;
  hasRMFunctionality?: boolean;
  brokerAdminfirstTimeLogin?: boolean;
  currentUrl: string = '';
  reportsList = [
    'Portfolio Valuation',
    'Portfolio Summary',
    'Capital Gain Realized',
    'Capital Gain Unrealized',
    'Transactions',
    'Dividend Report',
    'Profit And Loss',
    'Transaction History'
  ];
  showSubmenu: boolean = false;
  isExpanded!: boolean;
  activeLink: string = '';
  myMatchOptions: IsActiveMatchOptions = {
    queryParams: 'exact',
    matrixParams: 'ignored',
    paths: 'exact',
    fragment: 'ignored'
  };

  logoData: LogoData | null | undefined;
  menuItems: MenuItem[] = [];
  private subscriptions: Subscription[] = [];
  private logoFetched: boolean = false;
  private authUserService = inject(AuthUserService);
  private dialog = inject(DialogService);
  private router = inject(Router);
  private sharedModuleService = inject(SharedModuleService);
  private sideBarToggleService = inject(SideBarToggleService);

  ngOnInit(): void {
    const logoSub = this.sharedModuleService
      .getLogoData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data) {
          this.logoData = data;
        }
      });
    this.subscriptions.push(logoSub);

    // Track navigation changes for active menu highlighting
    const routerSub = this.router.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.currentUrl = event.url;
          this.isReportsActive = this.currentUrl.includes(
            '/reports/portfolio-valuation-report'
          );
        }
      });
    this.subscriptions.push(routerSub);

    const userSub = this.authUserService
      .getUserDetailBehaviorSubject()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data) {
          this.role = data.newRoleFinal.roleDescription;
          this.hasRMFunctionality = data.hasRMFunctionality;
          this.userId = data.userId;

          // Fetch organization logo for client users (only once)

          if (this.role === 'BROKER ADMIN') {
            this.roleRoute = 'broker';
          } else if (this.role === 'RELATIONSHIP MANAGER') {
            this.roleRoute = 'relationshipManager';
          } else {
            this.roleRoute = 'advisor';
          }
        }
      });
    this.subscriptions.push(userSub);
    const sidebarSub = this.sideBarToggleService
      .getIsExpandedUpdate()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: SidebarToggleResponse) => {
        this.isExpanded = res.text;
      });
    this.subscriptions.push(sidebarSub);
    if (this.role === USERROLES.CLIENT) {
      this.clientId = this.userId;

      // Use cached score if available to avoid duplicate API calls
      const cached = localStorage.getItem('profilescore');
      if (cached && cached !== 'Risk Profile Not Generated.') {
        this.profileScore = cached;
      } else {
        const payload = {
          userId: this.clientId
        };
        const riskSub = this.sharedModuleService
          .getClientRiskAssessmentScore(payload)
          .subscribe((res) => {
            if (res) {
              this.profileScore = res.message;
              localStorage.setItem('profilescore', res.message || '');
              localStorage.setItem('clientRiskProfile', JSON.stringify(res));
            }
          });
        this.subscriptions.push(riskSub);
      }
    }
    if (this.role === USERROLES.CLIENT && !this.logoFetched) {
      this.loadClientOrgLogo();
    }
  }

  /** Cleanup subscriptions to prevent memory leaks */
  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    sessionStorage.removeItem('orgLogoData');
  }

  isPurchaseActive(): boolean {
    return this.router.url.includes('/transaction/Purchase');
  }

  setOpenState(stateNumber: number) {
    this.state = stateNumber;
  }

  transactionRouting() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    this.router.onSameUrlNavigation = 'reload';
  }

  addRM(buttonType: string) {
    // Navigate to the appropriate onboarding page instead of opening modal
    if (buttonType == 'Send Email Invitation Broker Admin') {
      // Check user role to determine correct navigation path for advisor/broker onboarding
      if (this.role === USERROLES.ADVISOR) {
        // For advisors, use the advisor route for advisor onboarding
        this.router.navigate(['/advisor/onboard-advisor']);
      } else {
        // For other roles (BROKER_ADMIN, etc.), use the broker route
        this.router.navigate(['/broker/onboard-broker']);
      }
      return;
    } else if (buttonType == 'Send Email Invitation') {
      // Check user role to determine correct navigation path for RM onboarding
      if (this.role === USERROLES.ADVISOR) {
        // For advisors, use the advisor route
        this.router.navigate(['/advisor/onboard-rm']);
      } else {
        // For other roles (BROKER_ADMIN, etc.), use the relationshipManager route
        this.router.navigate(['/relationshipManager/onboard-rm']);
      }
      return;
    }

    // For other button types, keep the original modal behavior
    this.selectedModal = buttonType;
    this.isActive = true;
    if (buttonType == 'Send Email Invitation Broker Admin') {
      this.heading =
        this.role === USERROLES.ADVISOR
          ? 'Invite Advisor Admin'
          : 'Invite New Broker Admin';
      this.subheading =
        this.role === USERROLES.ADVISOR
          ? 'Provide the below required details to onboard an advisor admin for your organization.'
          : 'Provide the below required details to onboard a broker admin for your organization.';
    } else {
      this.heading = 'Invite New RM';
      this.subheading =
        'Provide the below required details to onboard a new RM for your organization.';
    }
    const inviteRmObj = {
      actionButton: buttonType,
      heading: this.heading,
      subheading: this.subheading
    };
    const dialogRef = this.dialog.open(InviteUpdateSidebarComponent, {
      data: { modalData: inviteRmObj },
      header: this.heading,
      width: '496px',
      height: '100%',
      position: 'right',
      modal: true,
      closable: true
    });
    dialogRef?.onClose.subscribe((result) => {
      this.isActive = false;
      if (result) {
        return;
      }
    });
  }
  crossClicked(): void {
    this.isExpanded = !this.isExpanded;
    this.sideBarToggleService.sendIsExpandedUpdate(this.isExpanded);
  }

  toggleSidebar(): void {
    this.isExpanded = !this.isExpanded;
    this.sideBarToggleService.sendIsExpandedUpdate(this.isExpanded);
  }

  /**
   * Clear transaction context when navigating via sidebar
   * This ensures breadcrumbs and forms reset properly
   */
  clearTransactionContext(): void {
    localStorage.removeItem('navigateFromHolding');
    localStorage.removeItem('transactionClientName');
    localStorage.removeItem('TransactionInfo');
    localStorage.removeItem('clientDataTransaction');
  }

  /**
   * Clear client portfolio related localStorage items
   * Called when navigating away from client portfolio views
   */
  clearClientPortfolioStorage(): void {
    localStorage.removeItem('profilescore');
    localStorage.removeItem('clientRiskProfile');
    localStorage.removeItem('clientCodeBse');
    localStorage.removeItem('organizationArn');
    localStorage.removeItem('clientEmail');
    localStorage.removeItem('clientDataCsv');
    localStorage.removeItem('clientBankData');
    localStorage.removeItem('clientFatcaData');
    localStorage.removeItem('clientNomineeData');
    localStorage.removeItem('clientFatcaTaxDetails');
    localStorage.removeItem('clientCsvId');
    localStorage.removeItem('clientDetails');
    sessionStorage.removeItem('orgLogoData');
  }

  /**
   * Loads organization logo for client users based on subdomain
   * Implements caching to prevent multiple API calls
   */
  private loadClientOrgLogo(): void {
    // Check if logo is already cached in localStorage
    const cachedLogo = localStorage.getItem('orgLogoData');
    if (cachedLogo) {
      try {
        const logoData = JSON.parse(cachedLogo) as LogoData;
        this.sharedModuleService.setLogoData(logoData);
        this.logoFetched = true;
        return;
      } catch (error) {
        console.error('Error parsing cached logo data:', error);
      }
    }

    // First try to get subdomain from roleDetails
    const roleDetailsStr = localStorage.getItem('roleDetails');

    let subdomain: string | null = null;

    if (roleDetailsStr) {
      try {
        const roleDetails = JSON.parse(roleDetailsStr) as RoleDetailsStorage;

        subdomain = roleDetails.subDomainName || null;
      } catch (error) {
        console.error('Error parsing role details:', error);
      }
    }

    // If subdomain exists, fetch organization logo
    if (subdomain) {
      const logoSub = this.sharedModuleService
        .getOrgLogoDetails(subdomain)
        .subscribe({
          next: (res) => {
            if (res?.base64Image) {
              // Format base64 image with proper data URI prefix if needed
              let imageData = res.base64Image;
              if (!imageData.startsWith('data:')) {
                imageData = `data:image/png;base64,${imageData}`;
              }
              const logoData = {
                img: imageData,
                tagline: res.slogan?.trim() || 'Most Trusted Investment Portal',
                base64Image: res.base64Image
              };
              this.sharedModuleService.setLogoData(logoData);
              // Cache the logo data
              localStorage.setItem('orgLogoData', JSON.stringify(logoData));
              this.logoFetched = true;
            } else {
              this.setDefaultLogo();
            }
          },
          error: () => {
            this.setDefaultLogo();
          }
        });
      this.subscriptions.push(logoSub);
    } else {
      this.setDefaultLogo();
    }
  }

  /**
   * Sets default GemFin logo
   */
  private setDefaultLogo(): void {
    const defaultLogoData = {
      img: 'assets/images/gemfin-logo.svg',
      tagline: 'Most Trusted Investment Portal',
      base64Image: ''
    };
    this.sharedModuleService.setLogoData(defaultLogoData);
    this.logoFetched = true;
  }

  backRoute(): void {
    this.clearClientPortfolioStorage();
    if (this.role === USERROLES.BROKER_ADMIN) {
      this.router.navigate(['broker/clients']);
    } else if (this.role === USERROLES.ADVISOR) {
      this.router.navigate(['advisor/clients']);
    } else if (this.role === USERROLES.RELATIONSHIP_MANAGER) {
      this.router.navigate(['relationshipManager/clients']);
    } else if (this.role === USERROLES.CLIENT) {
      this.router.navigate(['/client/profile']);
    } else {
      this.router.navigate(['/product-admin/dashboard']);
    }
  }
}
